import {
  GenerationProgress,
  useTimedStep,
} from "@rosc/ui/v32/generation-progress";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

export type GeneratedDocumentKind =
  | "Referral Letter"
  | "Patient Summary"
  | "note";

// A document written in one request (Create → Referral letter, Patient
// summary, Generate new note): the API reports no stages, so after 3 s
// "Reading the transcript…" gives way to "Writing …", which stays current
// until the document arrives. These documents get no value checks, so no
// "Checking values…" step.
export const DOCUMENT_STEP_DELAYS = [3000] as const;

const WORDING = {
  "Referral Letter": {
    title: "generationProgress.referral.title",
    writing: "generationProgress.steps.writingLetter",
  },
  "Patient Summary": {
    title: "generationProgress.summary.title",
    writing: "generationProgress.steps.writingSummary",
  },
  note: {
    title: "generationProgress.newNote.title",
    writing: "generationProgress.steps.writingNote",
  },
} as const;

export const DocumentGenerationProgress = ({
  kind,
  opensHere = true,
  className,
}: {
  kind: GeneratedDocumentKind;
  // "It opens here when it's ready": on the visit page, not in a sheet that
  // closes when the document arrives.
  opensHere?: boolean;
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const current = useTimedStep(DOCUMENT_STEP_DELAYS, true, kind);
  const wording = WORDING[kind];
  return (
    <GenerationProgress
      className={className}
      current={current}
      description={opensHere ? t("generationProgress.opensHere") : undefined}
      steps={[
        { key: "reading", label: t("generationProgress.steps.reading") },
        { key: "writing", label: t(wording.writing) },
      ]}
      testId="document-generating"
      title={t(wording.title)}
    />
  );
};
